import React, { useCallback, useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import { FormattedMessage } from 'react-intl'
import KanBoIcon from 'components/KanBoIcon'
import {
  ToggleModal
} from 'components/Modal'
import DateConflicts from '../DateConflicts'
import CardDate from '../../CardDate'
import * as CardStatusRoles from 'const/CardStatusRoles'
import Button from 'components/Button'
import withLeftSidebar, { LeftSidebarProps } from 'components/Sidebar/withLeftSidebar'
import { ModalBoardContext, ModalCardContext } from 'components/Modal'
import requestChangeStartDate from '../../sagas/requestChangeStartDate'
import requestRemoveStartDate from '../../sagas/requestRemoveStartDate'
import { useDispatch, useSelector } from 'react-redux'
import { useCardContext } from 'dataProviders/Card'
import createCardDateConflictsSelector from '../../selectors/createCardDateConflictsSelector'
import createStatusSelector from '../../selectors/createStatusSelector'
import { createSelector } from 'reselect'
import createIsCardCompletedSelector from '../../selectors/createIsCardCompletedSelector'

const TypedCardDate: any = CardDate
const TypedDateConflicts: any = DateConflicts

const getCardId = (state, { cardId }) => cardId

const StartDate: React.FC<LeftSidebarProps> = ({
  leftSidebar,
}) => {
  const dispatch = useDispatch()

  const [nowMoment, setNowMoment] = useState(moment())
  const [showModal, setShowModal] = useState(false)

  const cardContext = useCardContext()

  const boardId = useSelector(state => state['view-board'].cards[cardContext.id]?.boardId)
  const isArchived = useSelector(state => state['view-board'].cards[cardContext.id]?.isArchived)
  const isTemplate = useSelector(state => state['view-board'].cards[cardContext.id]?.isTemplate)
  const isBoardClosed = useSelector(state => state['view-board'].view[state['view-board'].cards[cardContext.id]?.boardId]?.isClosed)
  const startDate = useSelector(state => state['view-board'].cards[cardContext.id]?.startDate)
  const startDateId = useSelector(state => state['view-board'].cards[cardContext.id]?.startDateId)

  const statusSelector = useMemo(() => createStatusSelector(getCardId), [])

  const status = useSelector(state => statusSelector(state, {
    cardId: cardContext.id,
  }))

  const dateConflictsSelector = useMemo(() => createCardDateConflictsSelector(), [])

  const dateConflicts = useSelector(state => dateConflictsSelector(state, {
    boardId,
    cardId: cardContext.id,
    startDate,
  }))

  const dateConflictCountSelector = useMemo(() => createSelector(
    [dateConflictsSelector],
    (dateConflicts) =>
    dateConflicts.reduce((count, errorCard) => count + errorCard.errors.length, 0)
  ), [dateConflictsSelector])

  const dateConflictCount = useSelector(state => dateConflictCountSelector(state, {
    boardId,
    cardId: cardContext.id,
    startDate,
  }))

  const isCardCompletedSelector = useMemo(() => createIsCardCompletedSelector(getCardId), [])

  const isStartAfterDueDateSelector = useMemo(() => createSelector(
    [
      isCardCompletedSelector,
      (state, { cardId }) => state['view-board'].cards[cardContext.id]?.startDate,
      (state, { cardId }) => state['view-board'].cards[cardContext.id]?.dueDate,
    ],
    (
      isCardCompleted,
      startDate,
      dueDate, 
    ) =>
      (startDate && dueDate && !isCardCompleted) ? startDate > dueDate : false
  ), [isCardCompletedSelector])

  const isStartAfterDueDate = useSelector(state => isStartAfterDueDateSelector(state, {
    cardId: cardContext.id,
  }))

  useEffect(() => {
    const timer = setInterval(() => {
      setNowMoment(moment())
    }, 10000)

    return () => {
      clearInterval(timer)
    }
  }, [])

  const handleRemoveDateClick = () => {
    dispatch(requestRemoveStartDate({
      cardId: cardContext.id,
    }))
  }

  const handleDateChange = useCallback((date) => {
    dispatch(requestChangeStartDate({
      cardId: cardContext.id,
      startDate: date
    }))
  }, [])

  const handleCardsOutOfTimeFrameClick = useCallback((event) => {
    event.stopPropagation()

    leftSidebar.push(() =>
      <ModalBoardContext boardId={boardId}>
        <ModalCardContext cardId={cardContext.id}>
          <DateConflicts
            errorCards={dateConflicts}
            title={
              <FormattedMessage id='CARD_DATE_CONFLICTS_START_DATE' values={{ count: dateConflictCount }} />
            }
          />
        </ModalCardContext>
      </ModalBoardContext>
    )
  }, [leftSidebar, boardId, dateConflicts, dateConflictCount])

  const handleModalToggle = useCallback((show) => {
    if (show === false) {
      setShowModal(false)
    }
  }, [])

  let dateStyle: string | undefined = undefined
  if (
    !isArchived &&
    !isTemplate &&
    !isBoardClosed && 
    startDate &&
    (status
      ? status.role === CardStatusRoles.NOT_STARTED
      : false
    )
  ) {
    if (moment(startDate).isSameOrBefore(nowMoment)) {
      dateStyle = 'danger'
    } else if (moment(startDate).diff(nowMoment, 'days') <= 7) {
      dateStyle = 'warning'
    }
  }

  return startDate ? (
    <ToggleModal
      backdrop
      attachClickHandler={false}
      show={showModal}
      onToggle={handleModalToggle}
      attachment='top left'
      targetAttachment='top right'
      constraints={[
        {
          to: 'window',
          pin: true
        }
      ]}
    >
      <TypedCardDate
        id={startDateId}
        icon={<KanBoIcon name='kanbo-16-start-date' />}
        name={<FormattedMessage id='CARD_START_DATE' />}
        style={dateStyle}
        subtitle={
          <>
            {isStartAfterDueDate ? (
                <span
                  className='text-danger'
                >
                  <FormattedMessage
                    id='DATE_START_AFTER_DUE'
                  />
                </span>
            ) : null}
            {dateConflicts.length > 0 ? (
              <Button
                bsSize='xsmall'
                bsStyle='warning'
                onClick={handleCardsOutOfTimeFrameClick}
              >
                <FormattedMessage id='CARD_DATE_CONFLICTS' values={{ count: dateConflictCount }} />
              </Button>
            ) : null}
          </>
        }
        date={startDate}
        onRemove={handleRemoveDateClick}
        onDateChange={handleDateChange}
      />
      <TypedDateConflicts
        cardId={cardContext.id}
        errorCards={dateConflicts}
        title={
          <FormattedMessage id='CARD_DATE_CONFLICTS_DUE_DATE' values={{ count: dateConflictCount }} />
        }
      />
    </ToggleModal>
  ) : null
}

export default withLeftSidebar(StartDate)
